import { useEffect, useRef, useState } from 'react';
import PinDigits from './PinDigits';
import { securityErrorMessage } from './merchantSecurity';

export default function SecurityChallenge({ client, purpose, method, email, request, onVerified, onSessionError, polished = false, autoStart = false, submitLabel = '인증 확인', onBusyChange, continuous = false, beforeSubmit }) {
    const [challenge, setChallenge] = useState(null);
    const [value, setValue] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [nextSend, setNextSend] = useState(0);
    const [now, setNow] = useState(Date.now());
    const locked = useRef(false);
    const alive = useRef(true);
    useEffect(() => { onBusyChange?.(busy); return () => onBusyChange?.(false); }, [busy, onBusyChange]);
    const digits = method === 'PIN' ? 4 : 6;
    useEffect(() => {
        alive.current = true;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => { alive.current = false; clearInterval(timer); };
    }, []);
    const fail = failure => {
        setValue(''); setError(securityErrorMessage(failure));
        if (failure?.code === 4100) setChallenge(null);
        if (failure?.code === 4290) setNextSend(Date.now() + 60000);
        if ([9999, 4280, 4281, 4230, 4032, 4091, 5033].includes(failure?.code)) onSessionError?.(failure);
    };
    const start = async () => {
        if (locked.current || Date.now() < nextSend) return;
        locked.current = true; setBusy(true); setError(''); setValue(''); setChallenge(null);
        try {
            const body = { purpose, method };
            if (purpose === 'withdraw' || purpose === 'internal_transfer') body.request = request;
            if (purpose === 'enroll_email' && email?.trim()) body.email = email.trim();
            const data = await client.post('/challenge', body);
            if (typeof data?.challenge_id !== 'string' || !data.challenge_id || !Number.isFinite(Number(data.expires_in)) || !(Number(data.expires_in) > 0)) throw new Error('인증 요청 정보를 확인하지 못했습니다.');
            if (!alive.current) return;
            setNow(Date.now());
            setChallenge({ id: data.challenge_id, expiresAt: Date.now() + Number(data.expires_in) * 1000 });
            if (method === 'EMAIL') setNextSend(Date.now() + 60000);
        } catch (failure) { if (alive.current) fail(failure); }
        finally { locked.current = false; if (alive.current) setBusy(false); }
    };
    const startRef = useRef(start); startRef.current = start;
    const started = useRef(false);
    useEffect(() => {
        if (autoStart && method === 'PIN' && !started.current) {
            started.current = true; void startRef.current();
        }
    }, [autoStart, method]);
    const verify = async event => {
        event.preventDefault();
        if (locked.current || !challenge || !new RegExp(`^[0-9]{${digits}}$`).test(value)) return;
        if (challenge.expiresAt <= Date.now()) { setChallenge(null); setValue(''); setError('인증 시간이 만료되었습니다. 다시 요청해주세요.'); return; }
        locked.current = true; setBusy(true); setError('');
        try {
            const data = await client.post('/verify', { challenge_id: challenge.id, value });
            if (typeof data?.authorization !== 'string' || !data.authorization || data.purpose !== purpose || !Number.isFinite(Number(data.expires_in)) || !(Number(data.expires_in) > 0)) throw new Error('인증 권한을 확인하지 못했습니다.');
            if (!alive.current) return;
            setValue(''); setChallenge(null);
            await onVerified({ value: data.authorization, expiresAt: Date.now() + Number(data.expires_in) * 1000 });
        } catch (failure) { if (alive.current) fail(failure); }
        finally { locked.current = false; if (alive.current) setBusy(false); }
    };
    if (polished && method === 'PIN') {
        const seconds = challenge ? Math.max(0, Math.ceil((challenge.expiresAt - now) / 1000)) : 0;
        return <div className="pin-challenge">
            {error && <p role="alert" className="pin-error">{error}</p>}
            {challenge ? <form onSubmit={verify}>
                <PinDigits label="PIN 4자리" value={value} onChange={setValue} disabled={busy || seconds === 0} autoFocus continuous={continuous} />
                <div className="pin-timer" role="status">{seconds > 0 ? <>{continuous ? '남은 시간' : '입력 가능 시간'} <strong>{String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}</strong></> : '인증 시간이 만료되었습니다.'}</div>
                {beforeSubmit}
                {seconds > 0 ? <button className="pin-primary" type="submit" disabled={busy || value.length !== 4}>{busy ? '확인 중…' : submitLabel}</button>
                    : <button className="pin-primary" type="button" onClick={start} disabled={busy || now < nextSend}>다시 인증하기</button>}
            </form> : <>{beforeSubmit}{busy ? <p className="pin-loading" role="status">인증을 준비하고 있습니다…</p>
                : <button className="pin-primary" type="button" onClick={start} disabled={now < nextSend}>{now < nextSend ? `${Math.ceil((nextSend - now) / 1000)}초 후 다시 시도` : '다시 인증하기'}</button>}</>}
        </div>;
    }
    return <div className="security-challenge">
        {error && <p role="alert">{error}</p>}
        <button type="button" className="btn btn-outline-secondary" onClick={start} disabled={busy || now < nextSend}>
            {method === 'EMAIL' ? '이메일 인증번호 발송' : 'PIN 인증 시작'}{now < nextSend ? ` (${Math.ceil((nextSend - now) / 1000)}초)` : ''}
        </button>
        {challenge && <form onSubmit={verify}>
            <label>{method === 'PIN' ? 'PIN 4자리' : '이메일 인증번호 6자리'}
                <input autoComplete="off" type={method === 'PIN' ? 'password' : 'text'} inputMode="numeric" maxLength={digits}
                    className="form-control mt-2" value={value} disabled={busy} onChange={e => setValue(e.target.value.replace(/[^0-9]/g, ''))} />
            </label>
            <p>유효시간 {Math.max(0, Math.ceil((challenge.expiresAt - now) / 1000))}초</p>
            <button type="submit" className="btn btn-primary mt-2" disabled={busy || value.length !== digits || challenge.expiresAt <= now}>인증 확인</button>
        </form>}
    </div>;
}
